import { useEffect } from 'preact/hooks';
import { useSignal } from '@preact/signals';
import cn from 'classnames';
import type { Product } from '@/domain/Product.ts';
import HeroLayout from './HeroLayout/HeroLayout.tsx';
import isPreview from '@/widgets/utils/isPreview.ts';
import styles from './ProductDetailsHero.module.scss';
import Providers, { type ProductDetailsPageProps } from './Providers.tsx';
import { trackProductView } from './Tracking.ts';

export type ProductDetailsHeroProps = ProductDetailsPageProps & {
  product: Product;
};

export default function ProductDetailsHero({
  route,
  product,
  config,
  marketConfig,
  staticContent
}: ProductDetailsHeroProps) {
  const $props = useSignal<ProductDetailsHeroProps>({
    route,
    marketConfig,
    config,
    product,
    staticContent
  });

  const $previewLoading = useSignal<boolean>(false);

  useEffect(() => {
    // prevent rerendering causing duplicate tracking
    if (!window.trackedProductView) {
      trackProductView(
        product,
        marketConfig.financialSettings.defaultCurrency,
        marketConfig.discoveryOfferBundleProducts?.includes(product.id)
      );
      window.trackedProductView = true;
    }
  }, [product, marketConfig.financialSettings.defaultCurrency]);

  useEffect(() => {
    if (isPreview()) {
      (async () => {
        try {
          $previewLoading.value = true;
          const preview = await import('./Preview.ts');
          const previewData = await preview.getPreviewData(route);

          if (!previewData.product) {
            throw new Error('Product not found');
          }

          // @ts-ignore
          $props.value = {
            route,
            ...previewData
          };
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
          // eslint-disable-next-line no-alert
          globalThis.alert('Failed to load preview');
        } finally {
          $previewLoading.value = false;
        }
      })();
    }
  }, [globalThis?.location?.search]);

  return (
    <Providers {...$props.value}>
      <div
        class={cn(styles.container, {
          [styles.previewLoading]: $previewLoading.value
        })}
      >
        <HeroLayout product={$props.value.product} />
      </div>
    </Providers>
  );
}
